import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Modal, message, Radio, Input } from "antd";
import { useForm, Controller } from "react-hook-form";
import { EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  clearInformationData,
  fetchGetCompanyInfo,
} from "reducers/informationSlice";
import { UTCToJapanTime } from "common/helper/time.helper";
import { UserTypeEnum } from "common/enums/UserType.enum";
import { CompanyInfoStatusEnum } from "common/enums/CompanyInfoStatus.enum";
import { INDUSTRY_OPTIONS } from "common/enums/Industry.enum";
import { API_ENDPOINTS, FILE_BASE_URL } from "config/apiConfig";
import { companyInfoStatusMap } from "common/helper/text.helper";
import { axiosInstance } from "config/axiosConfig";
import { useCommonContext } from "contexts/CommonContext";
import CustomBreadCrumb from "components/CustomBreadCrumb";

const DetailReviewCompanyPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const { companyReviewData } = useSelector(
    (state: RootState) => state.information,
  );
  const { companyCode } = useParams();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewType, setPreviewType] = useState<"image" | "pdf">("image");
  const [modalTitle, setModalTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  type ReviewFormType = { result: "approve" | "reject"; reason?: string };
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<ReviewFormType>({
    defaultValues: { result: "approve", reason: "" },
  });

  useEffect(() => {
    if (companyCode) {
      dispatch(fetchGetCompanyInfo(companyCode));
    }
    setHeaderTitle("ユーザー審査");
    return () => {
      dispatch(clearInformationData());
    };
  }, []);

  const createdAtFormated = (utcTime: string) => {
    if (!utcTime) {
      return "";
    }
    const { year, month, day, hour, minute } = UTCToJapanTime(utcTime);
    return `${year}/${month}/${day} ${hour}:${minute}`;
  };

  const getFileType = (url: string): "image" | "pdf" => {
    if (!url) return "image";
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes(".pdf") || lowerUrl.includes("pdf")) {
      return "pdf";
    }
    return "image";
  };

  const handlePreview = (url: string, title: string) => {
    if (!url) {
      message.warning("ファイルのURLが見つかりません");
      return;
    }
    const fullUrl = `${FILE_BASE_URL}/${url}`;
    setPreviewUrl(fullUrl);
    setPreviewType(getFileType(fullUrl));
    setModalTitle(title);
    setIsModalVisible(true);
  };

  // Download file
  const handleDownload = async (url: string, filename: string) => {
    try {
      if (!url) {
        message.warning("ファイルのURLが見つかりません");
        return;
      }

      if (!url.includes("http")) {
        url = `${FILE_BASE_URL}/${url}`;
      }

      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      message.success("ダウンロードを開始しました");
    } catch (error) {
      message.error("ダウンロードに失敗しました");
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setPreviewUrl("");
    setModalTitle("");
  };

  const onSubmit = async (values: {
    result: "approve" | "reject";
    reason?: string;
  }) => {
    if (values.result === "reject" && !values.reason?.trim()) {
      message.error("否認理由を入力してください");
      return;
    }

    if (!companyCode) {
      message.error("会社コードが見つかりません");
      return;
    }

    setIsSubmitting(true);

    const companyStatus =
      values.result === "approve"
        ? CompanyInfoStatusEnum.APPROVED
        : CompanyInfoStatusEnum.REJECTED;

    axiosInstance
      .put(`${API_ENDPOINTS.COMPANY_INFO}/${companyCode}`, {
        reason: values.reason,
        status: companyStatus,
      })
      .then((res) => {
        if (res.status === 200) {
          message.success("審査結果を送信しました");
          navigate(-1);
        }
      })
      .catch(() => {
        message.error("審査結果の送信に失敗しました");
      });
  };

  const FileActionButtons = ({
    url,
    filename,
  }: {
    url: string;
    filename: string;
  }) => {
    if (!url) {
      return <span className="text-gray-400">ファイルなし</span>;
    }

    return (
      <div className="flex flex-col md:flex-row items-center gap-2">
        <Button
          type="link"
          icon={<EyeOutlined className={"text-primary font-bold"} />}
          onClick={() => handlePreview(url, filename)}
          className="p-0 h-auto text-primary"
        >
          <span className="text-primary underline">プレビュー表示</span>
        </Button>
        <Button
          type="link"
          icon={<DownloadOutlined className={"text-primary font-bold"} />}
          onClick={() => handleDownload(url, filename)}
          className="p-0 h-auto "
        >
          <span className="text-primary underline">ダウンロード</span>
        </Button>
      </div>
    );
  };

  return (
    <div className="flex min-h-[752px] px-5 pt-5 md:p-[40px] pb-[100px] flex-col items-center gap-[30px] self-stretch">
      {/*breadcrumb*/}
      <CustomBreadCrumb
        items={[
          {
            label: "ユーザー審査",
            link: "/admin/review-company",
          },
          {
            label: `ユーザー審査詳細（ID：#${companyReviewData?.data.code}）`,
            isCurrent: true,
          },
        ]}
      />

      {/*table*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                申請者情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              申請ID
            </div>
            <div className="flex-1 p-4">#{companyReviewData?.data.code}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              申請日
            </div>
            <div className="flex-1 p-4">
              {createdAtFormated(companyReviewData?.data.created_at)}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ユーザー種別
            </div>
            {companyReviewData?.data.type === UserTypeEnum.COMPANY_BUYER ? (
              <div className="flex-1 p-4">買い手</div>
            ) : (
              ""
            )}
            {companyReviewData?.data.type === UserTypeEnum.COMPANY_SELLER ? (
              <div className="flex-1 p-4">売り手</div>
            ) : (
              ""
            )}
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              申請ステータス
            </div>
            <div className="flex-1 p-4">
              {companyInfoStatusMap[companyReviewData?.data.status] || ""}{" "}
            </div>
          </div>
        </div>
      </div>

      {/*table*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                法人情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              法人名
            </div>
            <div className="flex-1 p-4">
              {companyReviewData?.data.company_name}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              所在地
            </div>
            <div className="flex-1 p-4">{companyReviewData?.data.address}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              業種
            </div>
            <div className="flex-1 p-4">
              {INDUSTRY_OPTIONS[companyReviewData?.data.industry]?.label}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              担当者名
            </div>
            <div className="flex-1 p-4">
              {companyReviewData?.data.contact_first_name +
                " " +
                companyReviewData?.data.contact_last_name}
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              電話番号
            </div>
            <div className="flex-1 p-4">
              {companyReviewData?.data.phone_number}
            </div>
          </div>
        </div>
      </div>

      {/*table*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                ログイン情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              メールアドレス
            </div>
            <div className="flex-1 p-4">{companyReviewData?.data.email}</div>
          </div>
        </div>
      </div>

      {/*table - 提出書類*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                提出書類
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              マイナンバーカード
            </div>
            <div className="flex-1 p-4">
              <FileActionButtons
                url={companyReviewData?.data.number_card_url}
                filename="マイナンバーカード プレビュー"
              />
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              法人登記簿謄本
            </div>
            <div className="flex-1 p-4">
              <FileActionButtons
                url={companyReviewData?.data.corp_regist_cert_url}
                filename="法人登記簿謄本"
              />
            </div>
          </div>
        </div>
      </div>

      {/*table doing*/}
      {companyReviewData?.data.status ===
      CompanyInfoStatusEnum.PENDING_REVIEW ? (
        <div className="flex flex-col items-start gap-5 self-stretch">
          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                  審査
                </div>
              </div>
            </div>
          </div>
          <div className="station-custom-radio overflow-hidden w-full">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="grid grid-cols-1 gap-4"
            >
              {/* 審査結果 */}
              <div className="flex flex-col md:flex-row md:gap-[20px]">
                <div className="flex flex-col w-[90px] md:w-[150px] font-bold text-[16px] mb-2">
                  審査結果
                </div>
                <Controller
                  name="result"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      {...field}
                      className="flex flex-col md:flex-row gap-6 text-[16px] gap-[19px]"
                    >
                      <Radio className="" value="approve">
                        承認する
                      </Radio>
                      <Radio className="" value="reject">
                        否認する
                      </Radio>
                    </Radio.Group>
                  )}
                />
              </div>

              {/* 否認理由 */}
              <div className="flex flex-col md:flex-row md:gap-[20px]">
                <div className="flex flex-col md:w-[180px]">
                  <span className="font-bold text-[15px] md:text-[16px]">
                    否認理由
                  </span>
                  <span className="text-neutral-500 text-[11px] md:text-[12px] ">
                    ※否認を選んだ場合は必須
                  </span>
                </div>
                <Controller
                  name="reason"
                  control={control}
                  rules={{
                    validate: (v) =>
                      getValues("result") === "reject"
                        ? !!v?.trim() || "否認理由を入力してください"
                        : true,
                    maxLength: {
                      value: 1000,
                      message: "1000文字以内で入力してください",
                    },
                  }}
                  render={({ field }) => (
                    <Input.TextArea
                      {...field}
                      autoSize={{ minRows: 8, maxRows: 10 }}
                      placeholder="例：書類に不備があります／法人名が一致していません など"
                      status={errors.reason ? "error" : ""}
                      className="text-[16px]"
                    />
                  )}
                />
              </div>

              {/* Guide */}
              <div className="rounded-md bg-[#F5F5F5] text-primary p-[20px] md:px-[50px] md:py-[30px] md:p-5">
                <div className="font-bold mb-2 text-[18px] md:text-[20px]">
                  審査結果は申請者に自動で通知されます。
                </div>
                <ul className="text-[13px] md:text-[14px] list-disc pl-5 space-y-1">
                  <li className="text-black">
                    承認：ログイン情報がメールで送信されます
                  </li>
                  <li className="text-black">
                    否認：否認理由がメールで通知されます
                  </li>
                </ul>
              </div>

              {/* Buttons */}
              <div className="flex-col-reverse md:flex-row self-stretch inline-flex justify-start items-start gap-3.5">
                <Button
                  block
                  type="primary"
                  htmlType="button"
                  className="max-w-[340px] min-h-[60px] bg-primary-light text-primary text-lg font-bold"
                  onClick={() => navigate(-1)}
                >
                  一覧へ戻る
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  className="max-w-[340px] min-h-[60px] bg-primary text-lg font-bold"
                >
                  {isSubmitting ? "送信中..." : "審査結果を送信する"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-5 self-stretch">
          <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
            <div className="flex border-b border-b-neutral-border">
              <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
                審査結果
              </div>
              <div className="flex-1 p-4">
                {companyInfoStatusMap[companyReviewData?.data.status] || ""}
              </div>
            </div>
            <div className="flex">
              <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
                否認理由
              </div>
              <div className="flex-1 p-4">{companyReviewData?.data.reason}</div>
            </div>
          </div>
          <Button
            block
            type="primary"
            htmlType="button"
            className="max-w-[340px] min-h-[60px] bg-primary-light text-primary text-lg font-bold"
            onClick={() => navigate(-1)}
          >
            一覧へ戻る
          </Button>
        </div>
      )}

      {/*end table doing*/}

      {/* File Preview Modal */}
      <Modal
        className="file-preview-modal"
        title={modalTitle}
        open={isModalVisible}
        onCancel={handleModalClose}
        closeIcon={null}
        width="90%"
        style={{ top: 20 }}
        footer={[
          <div
            key={1}
            className="flex justify-center flex-col items-center gap-[10px]"
          >
            <button
              key="download"
              onClick={() => handleDownload(previewUrl, "test")}
              className="w-full md:w-80 min-h-14 px-5 py-2.5 rounded-md flex justify-center items-center text-white text-lg font-bold font-['Noto_Sans_JP'] leading-7 bg-primary"
            >
              ダウンロードする
            </button>
            <button
              key="close"
              onClick={handleModalClose}
              className="text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug"
            >
              戻る
            </button>
          </div>,
        ]}
      >
        {previewUrl && (
          <div className="w-full md:h-[70vh]">
            {previewType === "pdf" ? (
              // PDF Viewer
              <iframe
                src={`${previewUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title="PDF Viewer"
              />
            ) : (
              // Image Viewer
              <div className="flex justify-center items-center h-full bg-gray-50">
                <img
                  src={previewUrl}
                  alt={modalTitle}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    message.error("画像の読み込みに失敗しました");
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DetailReviewCompanyPage;
